import { useCreateItem } from '@api/generics';
import API_ROUTES from '@api/routes';
import TextFieldValidation from '@components/TextFieldValidation/TextFieldValidation';
import { LoadingButton } from '@mui/lab';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid
} from '@mui/material';
import {
	MAX_CHARS_AUTHORIZATION,
	stringRequiredMaxCharsValidationScheme
} from '@utils/validation';
import { useFormik } from 'formik';
import { useToast } from 'hooks';
import React from 'react';
import { IPermission } from 'types/authorization';
import { IActionDialogProps } from 'types/common';
import { object } from 'yup';

const PermissionCreateDialog: React.FC<IActionDialogProps> = ({
	onCreate,
	onCancel
}) => {
	const { invokeToastableAction } = useToast();
	const generateItemMutation = useCreateItem<IPermission>(
		'permissions',
		API_ROUTES.v1.permissions.add
	);

	const formik = useFormik<IPermission>({
		initialValues: {
			name: '',
			description: ''
		},
		validationSchema: object({
			name: stringRequiredMaxCharsValidationScheme(MAX_CHARS_AUTHORIZATION)
		}),
		onSubmit: async (values: IPermission) => {
			const action = async () => generateItemMutation.mutateAsync(values);
			await invokeToastableAction(
				action,
				'permissions.action.add.dialog.toast',
				onCreate
			);
		}
	});

	return (
		<Dialog open maxWidth="xs" data-testid="permission-create-dialog">
			<DialogTitle>
				{intl.tr('permissions.action.add.dialog.title')}
			</DialogTitle>
			<DialogContent>
				<Box pt={2} pb={2}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<TextFieldValidation
								id="name"
								formik={formik}
								fullWidth
								autoFocus
								required
								label={intl.tr('fields.permissions.column.name')}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextFieldValidation
								id="description"
								formik={formik}
								fullWidth
								label={intl.tr('fields.permissions.column.description')}
							/>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button
					onClick={onCancel}
					data-testid="permissions-add-dialog-cancel-button"
				>
					{intl.tr('button.cancel')}
				</Button>
				<LoadingButton
					variant="contained"
					color="primary"
					loading={formik.isSubmitting}
					disabled={!formik.isValid}
					onClick={() => formik.handleSubmit()}
					data-testid="permissions-add-dialog-approve-button"
				>
					{intl.tr('button.add')}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
};

export default PermissionCreateDialog;
